/* eslint-disable react-native/no-color-literals */
/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/prop-types */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState, useEffect } from 'react';
import { Input } from 'react-native-elements';
import {
  formControl,
  transparentFormControl,
  formMultilineControl,
  formControlError,
  formControlFocus,
  formLabel,
  formLabelError,
  formControlContainer,
  formInputText,
  formInputTextError,
} from '@assets/styles/formStyles';
import FormLabel from '@components/form/common/FormLabel';
import {
  numberOnly,
  numberWithDecimal,
  stripLineBreaks,
} from '@global/helpers';
import { isWebPlatform } from '@global/screens';
import { translate } from '@global/translate';
import { useTranslationDisabled } from '@components/shared/TranslationContext';
import PropTypes from 'prop-types';

const propTypes = {
  label: PropTypes.string,
  editable: PropTypes.bool,
  isSelectBox: PropTypes.bool,
  isRequired: PropTypes.bool,
  multiline: PropTypes.bool,
  isNumeric: PropTypes.bool,
  isDecimal: PropTypes.bool,
  isTransparent: PropTypes.bool,
  hasError: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  containerStyle: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
    PropTypes.number,
  ]),
  style: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
    PropTypes.number,
  ]),
  errorStyle: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
    PropTypes.number,
  ]),
  labelStyle: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
    PropTypes.number,
  ]),
  inputContainerStyle: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
    PropTypes.number,
  ]),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  testID: PropTypes.string,
};

const defaultProps = {
  isTransparent: true,
  editable: true,
  isSelectBox: false,
  hasError: false,
  isNumeric: false,
  isDecimal: false,
  multiline: false,
  containerStyle: {},
  style: {},
  errorStyle: {},
  labelStyle: {},
  inputContainerStyle: {},
  value: '',
  testID: 'input-default',
};

const InputDefault = (props) => {
  const {
    editable = true,
    isSelectBox = false,
    hasError = false,
    isTransparent = true,
    isNumeric = false,
    isDecimal = false,
    multiline = false,
    containerStyle = {},
    style = {},
    errorStyle = {},
    labelStyle = {},
    inputContainerStyle = {},
    inputRef = null,
    value = '',
  } = props;
  const translationDisabled = useTranslationDisabled();

  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isFocus, setIsFocus] = useState(false);

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      if (hasError) {
        setIsError(true);
        setErrorMessage(hasError);
      } else {
        setIsError(false);
        setErrorMessage('');
      }
    }
    return () => {
      mounted = false;
    };
  }, [props]);

  const onFocus = () => {
    setIsFocus(true);
    setIsError(false);
    setErrorMessage('');
    try {
      props.onFocus();
    } catch (error) {}
  };

  const onBlur = () => {
    setIsFocus(false);
    try {
      // eslint-disable-next-line react/prop-types
      props.onBlur();
    } catch (error) {}
  };

  const onChangeText = (val) => {
    try {
      let finalValue = val;
      if (isDecimal) {
        finalValue = numberWithDecimal(val);
      }
      if (isNumeric) {
        finalValue = numberOnly(val);
      }
      // Single-line inputs: strip line breaks / tabs (e.g. from paste). Textareas pass
      // `multiline` and keep their newlines. (numeric/decimal values are already break-free.)
      if (!multiline) {
        finalValue = stripLineBreaks(finalValue);
      }
      // eslint-disable-next-line react/prop-types
      props.onChangeText(finalValue);
    } catch (error) {}
  };

  const formLabelErrorControl = isError ? formLabelError : {};
  const formControlErrorControl = isError ? formControlError : {};
  const formControlFocusControl =
    isFocus && !isError && editable ? formControlFocus : {};
  const formInputFocus = isWebPlatform ? { outline: 'transparent' } : {};
  const renderErrorMessageControl = Boolean(isError || hasError);
  const errorMessageControl = errorMessage || hasError;
  const errorMessageControlText = errorMessageControl
    ? errorMessageControl
    : '';
  const errorMessageStyle = errorMessageControl
    ? { ...formInputTextError, ...errorStyle }
    : {};

  return (
    <Input
      {...props}
      label={<FormLabel {...props} />}
      labelStyle={[formLabel, labelStyle, formLabelErrorControl]}
      inputContainerStyle={[
        isTransparent ? { ...transparentFormControl } : { ...formControl },
        formControlErrorControl,
        formControlFocusControl,
        inputContainerStyle,
        multiline ? formMultilineControl : {},
        !editable && !isSelectBox ? { backgroundColor: '#ebebeb' } : {},
      ]}
      containerStyle={[formControlContainer, containerStyle]}
      style={[formInputText, style, formInputFocus]}
      placeholder={
        translationDisabled ? props.placeholder : translate(props.placeholder)
      }
      errorStyle={errorMessageStyle}
      errorMessage={
        translationDisabled
          ? errorMessageControlText
          : translate(errorMessageControlText)
      }
      renderErrorMessage={renderErrorMessageControl}
      autoCapitalize={props.autoCapitalize ?? 'none'}
      autoCorrect={props.autoCorrect ?? false}
      ref={inputRef}
      value={value || value == 0 ? value.toString() : ''}
      onBlur={() => onBlur()}
      onFocus={() => onFocus()}
      onChangeText={(val) => onChangeText(val)}
    />
  );
};

InputDefault.propTypes = propTypes;
InputDefault.defaultProps = defaultProps;

export default React.memo(InputDefault, (prev, next) => prev == next);
